import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterComponent } from '@app/shared/components/filter/filter.component';
import { NotificationType, PaginationMeta } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { FilterService } from '@app/shared/services/filter/filter.service';
import { QuestionService } from '@app/shared/services/questions/question.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { DialogCreateIntentComponent } from '../../components/dialog-create-intent/dialog-create-intent.component';

@Component({
  standalone: false,
  selector: 'sbnb-questions',
  templateUrl: './questions.component.html',
  styleUrls: ['./questions.component.scss'],
})
export class QuestionsComponent implements OnInit {
  intents: any[];
  loading: boolean;
  currentOffset: number;
  searchCriteria: string;
  paginationData: PaginationMeta;
  activeFilters: Filter[] = [];
  segmentFilterDefaults: any;

  @ViewChild('filterComponent')
  filterComponent: FilterComponent;

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private titleService: TitleService,
    private route: ActivatedRoute,
    private questionService: QuestionService,
    private filter: FilterService,
    private toast: ToastNotificationsService,
    private location: Location
  ) {}

  ngOnInit() {
    this.loading = true;
    this.titleService.manuallySet('Questions');

    this.currentOffset = +this.route.snapshot.paramMap.get('offset') || 0;
    this.searchCriteria = this.route.snapshot.paramMap.get('query');

    this.activeFilters = [];
    this.segmentFilterDefaults = [];

    // If we stashed filters for this key, retrieve and set them
    const stashedFilters = this.filter.retrieveStashedValuesForKey('questions');
    if (stashedFilters) {
      this.activeFilters = stashedFilters;
      this.segmentFilterDefaults = this.activeFilters;
    }

    this.loadIntents(this.activeFilters, this.currentOffset, this.searchCriteria);
  }

  loadIntents(filters: Filter[], offset?: number, search?: string) {
    this.loading = true;

    this.questionService.getIntents(filters, offset, search).subscribe((res) => {
      if (res.error) {
        this.loading = false;
        this.toast.open(`Error fetching questions`, 'Dismiss', NotificationType.Error);
        this.loading = false;
        return;
      }

      this.intents = res.data;
      // this.paginationData = res.meta?.pagination;
      this.loading = false;
    });
  }

  public handleFilterChanged(filters: Filter[]) {
    this.activeFilters = filters;

    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('questions', this.activeFilters);

    // Retrieve conversations based on the new filters
    this.loadIntents(this.activeFilters, 0, this.searchCriteria);

    // Zero out the offset from the URL - we have fresh data so need to go back to offset 0
    const url = this.router
      .createUrlTree([this.searchCriteria ? { query: this.searchCriteria } : {}], {
        relativeTo: this.route,
      })
      .toString();

    this.location.go(url);
  }

  public searchUpdated(searchVal: string) {
    // This is already debounced by the child presentation component
    const url = this.router.createUrlTree([{ query: searchVal }], { relativeTo: this.route }).toString();

    this.location.go(url);

    this.searchCriteria = searchVal;

    this.loadIntents(this.activeFilters, 0, this.searchCriteria);
  }

  openCreateDialog() {
    const dialogRef = this.dialog.open(DialogCreateIntentComponent, {
      width: '720px',
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  filterReset() {
    this.activeFilters = [];
    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('questions', this.activeFilters);

    this.filterComponent.selfReset();
    this.ngOnInit();
  }
}
